import React, {useState, useEffect, useCallback} from 'react';
import {
    Tabs,
    Layout,
    Typography,
    Button,
    message,
    Modal,
    Row,
    Col,
    Spin,
    Space
} from 'antd';
import {ExclamationCircleFilled, EditOutlined} from '@ant-design/icons';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {membershipService, useAuth} from '../../../services';
import ChineseMasterTab from './ChineseMasterTab';
import EnglishMasterTab from './EnglishMasterTab';
import OthersTab from './OthersTab';
import ActualProductsTab from './ActualProductsTab';
import CustomProductsTab from './CustomProductsTab';
import DelegatesTab from './DelegatesTab';
import CertificationsTab from './CertificationsTab';
import EventsTab from './EventsTab';
import {MemGetMemberResponse, TabChangedFields} from "../../../model";
import {getEditableRoles} from "../../../utils/rolePermissions";

const {Title} = Typography;
const {Content} = Layout;
const {confirm} = Modal;

const MemberDetailPage: React.FC = () => {
    // 檢查用戶是否有編輯權限
    const {hasEditPermission} = useAuth();
    const {membershipId} = useParams<{ membershipId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [memberData, setMemberData] = useState<MemGetMemberResponse | null>(null);
    const [activeTab, setActiveTab] = useState<string>('1');
    const [isEditing, setIsEditing] = useState(false);
    const [changedFields, setChangedFields] = useState<TabChangedFields>({
        chineseMaster: new Set<string>(),
        englishMaster: new Set<string>(),
        others: new Set<string>(),
        actualProducts: new Set<string>(),
        customProducts: new Set<string>(),
        delegates: new Set<string>(),
        certifications: new Set<string>(),
        events: new Set<string>(),
    });

    // 載入會員資料
    const navigateBack = useCallback(() => {
        navigate('/membership-master', {state: {restoreMembershipSearch: true}});
    }, [navigate]);

    const fetchMemberData = useCallback(async (id: number) => {
        setLoading(true);
        try {
            const data = await membershipService.getMember({membershipId: id});
            if (data) {
                setMemberData(data);
            } else {
                message.error('無法取得會員資料');
                navigateBack();
            }
        } catch (error) {
            console.error('Failed to fetch member data:', error);
            message.error('載入會員資料時發生錯誤');
            navigateBack();
        } finally {
            setLoading(false);
        }
    }, [navigateBack]);

    useEffect(() => {
        console.log('MemberDetail: membershipId parameter is', membershipId);

        // 如果是測試路徑，使用預設值
        if (membershipId === 'test') {
            fetchMemberData(1); // 使用 ID 1 作為測試
            return;
        }

        // 正常情況下使用 URL 參數
        if (membershipId && membershipId !== 'undefined') {
            try {
                const id = parseInt(membershipId);
                if (!isNaN(id)) {
                    fetchMemberData(id);
                } else {
                    message.error('會員ID格式不正確');
                    navigateBack();
                }
            } catch (error) {
                console.error('ID parsing error:', error);
                message.error('會員ID格式不正確');
                navigateBack();
            }
        } else {
            message.error('無效的會員ID');
            navigateBack();
        }
    }, [membershipId, fetchMemberData, navigateBack]);

    // 開始編輯
    const startEditing = () => {
        setIsEditing(true);
    };

    // 取消編輯
    const cancelEditing = () => {
        if (Object.values(changedFields).some(fields => fields.size > 0)) {
            confirm({
                title: '放棄編輯內容',
                icon: <ExclamationCircleFilled/>,
                content: '您確定要放棄所有編輯內容嗎？',
                okText: '確定',
                cancelText: '取消',
                onOk() {
                    // 重新載入原始資料
                    if (membershipId) {
                        fetchMemberData(parseInt(membershipId));
                    }
                    // 清空所有已變更欄位
                    setChangedFields({
                        chineseMaster: new Set<string>(),
                        englishMaster: new Set<string>(),
                        others: new Set<string>(),
                        actualProducts: new Set<string>(),
                        customProducts: new Set<string>(),
                        delegates: new Set<string>(),
                        certifications: new Set<string>(),
                        events: new Set<string>(),
                    });
                    setIsEditing(false);
                },
            });
        } else {
            setIsEditing(false);
        }
    };

    // 處理頁籤切換
    const handleTabChange = (key: string) => {
        const hasChanges =
            changedFields.chineseMaster.size > 0 ||
            changedFields.englishMaster.size > 0 ||
            changedFields.others.size > 0 ||
            changedFields.actualProducts.size > 0 ||
            changedFields.customProducts.size > 0 ||
            changedFields.delegates.size > 0 ||
            changedFields.certifications.size > 0;

        if (hasChanges) {
            confirm({
                title: '尚有未儲存的變更',
                icon: <ExclamationCircleFilled/>,
                content: '切換頁籤將會遺失所有未儲存的變更，是否確定要切換？',
                okText: '確定',
                cancelText: '取消',
                onOk() {
                    // 清空所有已變更欄位
                    setChangedFields({
                        chineseMaster: new Set<string>(),
                        englishMaster: new Set<string>(),
                        others: new Set<string>(),
                        actualProducts: new Set<string>(),
                        customProducts: new Set<string>(),
                        delegates: new Set<string>(),
                        certifications: new Set<string>(),
                        events: new Set<string>(),
                    });
                    setActiveTab(key);

                    // 載入原始資料，確保所有變更都被還原
                    if (membershipId) {
                        fetchMemberData(parseInt(membershipId));
                    }
                },
            });
        } else {
            setActiveTab(key);
        }
    };

    // 標記變更的欄位
    const markChangedField = useCallback((tabKey: keyof TabChangedFields, fieldName: string, changed: boolean) => {
        setChangedFields(prev => {
            const newFields = {...prev};
            if (changed) {
                newFields[tabKey].add(fieldName);
            } else {
                newFields[tabKey].delete(fieldName);
            }
            return newFields;
        });
    }, []);

    const handleChineseChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('chineseMaster', fieldName, changed),
        [markChangedField]
    );
    const handleEnglishChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('englishMaster', fieldName, changed),
        [markChangedField]
    );
    const handleActualProductsChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('actualProducts', fieldName, changed),
        [markChangedField]
    );
    const handleDelegatesChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('delegates', fieldName, changed),
        [markChangedField]
    );
    const handleCertificationsChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('certifications', fieldName, changed),
        [markChangedField]
    );
    const handleOthersChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('others', fieldName, changed),
        [markChangedField]
    );
    const handleCustomProductsChanged = useCallback(
        (fieldName: string, changed: boolean) =>
            markChangedField('customProducts', fieldName, changed),
        [markChangedField]
    );

    // 清除某個頁籤的所有變更標記
    const clearTabChanges = (tabKey: keyof TabChangedFields) => {
        setChangedFields(prev => {
            const newFields = {...prev};
            newFields[tabKey] = new Set<string>();
            return newFields;
        });
    };

    if (loading) {
        return (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
                <Spin size="large" tip="載入中..."/>
            </div>
        );
    }

    const memberStatus = memberData?.suspensionDate
        ? {label: '停權', className: 'member-detail-suspended'}
        : memberData?.withdrawalDate
            ? {label: '退會', className: 'member-detail-withdrawn'}
            : null;

    return (
        <Layout className={`member-detail-layout ${memberStatus?.className || ''}`}>
            <Content className="member-detail-content">
                {/* 標題區域 */}
                <Row justify="space-between" align="middle" className="member-detail-header">
                    <Col flex="auto">
                        <Title level={3} className="member-detail-title">
                            會員主檔詳細資料 - {memberStatus ? `(${memberStatus.label})` : ''}
                            {memberData?.companyName || '未知會員'}
                            {memberData?.memberId ? `(${memberData.memberId})` : ''}
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                onClick={navigateBack}
                            >
                                返回清單
                            </Button>
                            {isEditing ? (
                                <Button
                                    type="primary"
                                    onClick={cancelEditing}
                                >
                                    取消
                                </Button>
                            ) : (
                                <Button
                                    type="primary"
                                    icon={<EditOutlined/>}
                                    onClick={startEditing}
                                    disabled={!hasEditPermission(getEditableRoles(location.pathname))} // 根據用戶角色禁用編輯按鈕
                                    title={!hasEditPermission(getEditableRoles(location.pathname)) ? '您沒有編輯權限' : '編輯'}
                                >
                                    編輯
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>

                {/* 頁籤區域 */}
                <div className="member-detail-tabs-container">
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        type="card"
                        items={[
                            {
                                key: "1",
                                label: "會員中文主檔",
                                children: memberData && (
                                    <ChineseMasterTab
                                        memberData={memberData}
                                        onDataChanged={handleChineseChanged}
                                        changedFields={changedFields.chineseMaster}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('chineseMaster');
                                            setIsEditing(false)
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "2",
                                label: "會員英文主檔",
                                children: memberData && (
                                    <EnglishMasterTab
                                        memberData={memberData}
                                        onDataChanged={handleEnglishChanged}
                                        changedFields={changedFields.englishMaster}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('englishMaster');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "4",
                                label: "會員實際產品檢視",
                                children: memberData && (
                                    <ActualProductsTab
                                        membershipId={parseInt(membershipId!)}
                                        onDataChanged={handleActualProductsChanged}
                                        changedFields={changedFields.actualProducts}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('actualProducts');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "6",
                                label: "會員代表",
                                children: memberData && (
                                    <DelegatesTab
                                        membershipId={parseInt(membershipId!)}
                                        onDataChanged={handleDelegatesChanged}
                                        changedFields={changedFields.delegates}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('delegates');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "7",
                                label: "會員認證檢視",
                                children: memberData && (
                                    <CertificationsTab
                                        membershipId={parseInt(membershipId!)}
                                        onDataChanged={handleCertificationsChanged}
                                        changedFields={changedFields.certifications}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('certifications');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "3",
                                label: "其他",
                                children: memberData && (
                                    <OthersTab
                                        memberData={memberData}
                                        onDataChanged={handleOthersChanged}
                                        changedFields={changedFields.others}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('others');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "5",
                                label: "工廠登記之產業類別",
                                children: memberData && (
                                    <CustomProductsTab
                                        membershipId={parseInt(membershipId!)}
                                        onDataChanged={handleCustomProductsChanged}
                                        changedFields={changedFields.customProducts}
                                        onSaveSuccess={() => {
                                            fetchMemberData(parseInt(membershipId!));
                                            clearTabChanges('customProducts');
                                        }}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            },
                            {
                                key: "8",
                                label: "參加活動檢視",
                                children: memberData && (
                                    <EventsTab
                                        membershipId={parseInt(membershipId!)}
                                        isEditing={isEditing && hasEditPermission(getEditableRoles(location.pathname))}
                                    />
                                )
                            }
                        ]}
                    />
                </div>
            </Content>
        </Layout>
    );
};

export default MemberDetailPage;
